import React from 'lib/react';
import {
  EVChip, EVSkeleton,
} from 'lib/components';
import { isEqual } from 'lodash';
import clsx from 'lib/clsx';
import {
  PRODUCT_IDS,
  PRODUCT_CATALOG_FILTER_EXCLUDED_IDS, // #genai
  STATUSES,
} from 'constants/AppConstants';
import { formatDate, parseDateFromSearchText } from 'utils/date.utils';
import Typography from 'commonComponents/typography';
import history from 'store/CreateHistory';
import { getWallsAddOnIdByProductId } from 'features/finalizeOrder/FinalizeOrder.utils'; // #genai
import {
  SUB_STATUSES, PAGE_SIZE_OPTIONS, EDIT_PITCH_PRODUCT_IDS,
} from './OrderHistory.constants';
import IdentifyButton from './components/IdentifyButton.component';
import CancelButton from './components/CancelButton.component';
import DetailsButton from './components/details-button';
import UploadButton from './components/UploadButton.component';
import EditPitchButton from './components/EditPitchButton.component';
import TrueDesignLink from './components/TrueDesignLink.component';
import UpgradeButton from './components/UpgradeButton.component';
import SitemapLink from './components/SitemapLink.component';
import SitemapApprovalButton from './components/SitemapApprovalButton.component';
import SubscriptionCell from './components/SubscriptionCell.component';

export const getOrderHistoryColumns = (t, classes) => [
  { label: t('order_submitted__report_number'), dataKey: 'reportIdCell' },
  { label: t('common__product'), dataKey: 'product' },
  { label: t('order_submitted__address'), dataKey: 'address' },
  { label: t('common__date'), dataKey: 'date' },
  { label: t('common__claim_id'), dataKey: 'claimNumber' },
  { label: t('common_delivery'), dataKey: 'delivery' },
  { label: t('common__cost'), dataKey: 'cost', cellProps: { className: classes.costCell } },
  { label: t('common__status'), dataKey: 'status' },
  { label: '', dataKey: 'actions', cellProps: { className: classes.actionsCell } },
];

export const getReportIDsForPage = (reportIds, pageNumber, pageSize) => {
  const startIndex = ((pageNumber - 1) * pageSize);
  const endIndex = (pageNumber * pageSize);
  return reportIds.slice(startIndex, endIndex);
};

export const getReportCountText = (reportIds, pageNumber, pageSize, t) => {
  const start = ((pageNumber - 1) * pageSize) + 1;
  const end = (pageNumber * pageSize);
  return `${Math.min(start, reportIds?.length)}-${Math.min(end, reportIds?.length)} of ${reportIds?.length} ${t('order_history__report_count')}`;
};

const getPendingStatusName = (statusID, subStatusID, primaryProductID) => {
  if (statusID !== STATUSES.Pending) return null;
  if (subStatusID === SUB_STATUSES.NeedToID) return 'order_history__action_needed';
  if (subStatusID === SUB_STATUSES.MeasurementData || subStatusID === SUB_STATUSES.CustomerResponse) {
    if (primaryProductID === PRODUCT_IDS.BlueprintResidential) return 'order_history__action_needed';
    return 'order_history__check_email';
  }
  if (subStatusID === SUB_STATUSES.ReportTypeChange || subStatusID === SUB_STATUSES.CreditCardFailure) {
    return 'order_history__check_email';
  }
  // Pending + SiteMap shows ACTION NEEDED (with Sitemap approval action button)
  return 'order_history__action_needed';
};

const escapeRegExp = (text) => {
  if (typeof text !== 'string') {
    return '';
  }
  return text.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&').replace(/\s/g, '\\s');
};

export const getHighlightedText = (text, searchStr) => {
  const escapedSearchText = escapeRegExp(searchStr);
  const searchRegex = new RegExp(`(${escapedSearchText})`, 'i');
  if (searchStr && searchRegex.test(text)) {
    const parts = text.split(searchRegex);

    return parts.map((part) => (
      searchRegex.test(part)
        ? <mark style={{ backgroundColor: '#FFECB7' }} key={part}>{part}</mark>
        : <React.Fragment key={part}>{part}</React.Fragment>
    ));
  }
  return text;
};

/** Cancel button will NOT be displayed for below cases:
1. Already Closed Orders
2. Completed Orders
3. InProcess - ReadyToCapturePayment and InProcess-ReadyToSend
4. Pending - CreditCardFailure */
export const isReportCancellable = (statusID, subStatusID) => (
  (Number(statusID) !== STATUSES.Closed)
  && (Number(statusID) !== STATUSES.Completed)
  && !(Number(statusID) === STATUSES.InProcess && [SUB_STATUSES.ReadyToCapturePayment, SUB_STATUSES.ReadyToSend].includes(Number(subStatusID)))
  && !(Number(statusID) === STATUSES.Pending && Number(subStatusID) === SUB_STATUSES.CreditCardFailure)
);

export const isCompletedOrReadyToCapturePayment = (statusID, subStatusID) => (
  (Number(statusID) === STATUSES.Completed)
  || (Number(statusID) === STATUSES.InProcess && Number(subStatusID) === SUB_STATUSES.ReadyToCapturePayment)
);

export const canEditPitches = (primaryProductID) => EDIT_PITCH_PRODUCT_IDS.includes(primaryProductID);

// #genai OH/Analytics display only — API Name is ambiguous for unified roof SKUs
export const ORDER_HISTORY_PRODUCT_DISPLAY_NAME_BY_ID = {
  [PRODUCT_IDS.Roof]: 'Roof 3D',
  [PRODUCT_IDS.Roof2D]: 'Roof 2D',
};

export const getOrderHistoryProductFilterDisplayName = (productId, apiName = '') => (
  ORDER_HISTORY_PRODUCT_DISPLAY_NAME_BY_ID[productId] || apiName
);

function getOrderHistoryProductDisplayLabel(
  primaryProductName,
  orderHistoryReportType,
  addOnProductName,
  addOnProductID,
  primaryProductID,
) {
  // Prefer orderHistoryReportType so OH label matches the report type shown for the order
  // (e.g. when primaryProductName is a broader/catalog name).
  const apiBase = (orderHistoryReportType || primaryProductName || '').trim();
  // #genai force Roof 3D / Roof 2D labels when API name is ambiguous
  const base = getOrderHistoryProductFilterDisplayName(primaryProductID, apiBase);
  const addon = addOnProductName != null && String(addOnProductName).trim() !== ''
    ? String(addOnProductName).trim()
    : '';
  // Only append add-on label for ESX (114). Other add-ons are already reflected in primaryProductName from the API.
  const shouldAppendEsxAddon = Boolean(addon) && Number(addOnProductID) === Number(PRODUCT_IDS.ESX);
  return shouldAppendEsxAddon ? `${base} and ${addon}` : base;
}

export const isReportSent = (subStatusID) => [SUB_STATUSES.Sent, SUB_STATUSES.SentPM, SUB_STATUSES.Submitted].includes(subStatusID);

const STATUS_CLASS_NAMES = {
  [STATUSES.Completed]: 'completed',
  [STATUSES.Closed]: 'closed',
  [STATUSES.InProcess]: 'inProcess',
  [STATUSES.Created]: 'created',
  [`${[STATUSES.Pending]}_order_history__action_needed`]: 'actionNeeded',
  [`${[STATUSES.Pending]}_order_history__check_email`]: 'checkEmail',
};

const shouldShowSitemapLink = (isReportMSO, statusID) => Boolean(Number(isReportMSO)) && statusID !== STATUSES.Closed;

const getAddressLine = ({
  address, city, state, zip,
}) => `${address}\n${city}, ${state} ${zip}`;

const getAddressLabel = (t, primaryProductID, building, isSubscription = false) => (
  <>
    <em>{t(primaryProductID === PRODUCT_IDS.BlueprintResidential ? 'blueprint_order__blueprint_id' : 'order_submitted__building_id')}</em>
    {isSubscription ? <span>{building}</span> : building}
  </>
);

const getAddressCell = ({
  row, t, classes, searchText, isSubscription = false,
}) => {
  const {
    orderId, signedOrderId, reportId, address, city, state, zip, building, isReportMSO, statusID, primaryProductID,
  } = row;
  const label = getAddressLabel(t, primaryProductID, building, isSubscription);
  const showSitemapLink = shouldShowSitemapLink(isReportMSO, statusID);
  const addressCellStyle = {
    whiteSpace: 'pre',
    ...(isSubscription && { textWrap: 'wrap' }),
  };

  return (
    <>
      {address && (
        <div style={addressCellStyle}>
          {getHighlightedText(getAddressLine({
            address,
            city,
            state,
            zip,
          }), searchText)}
        </div>
      )}
      {building && (
        showSitemapLink
          ? (
            <SitemapLink
              reportId={reportId}
              orderId={orderId}
              signedOrderId={signedOrderId}
              label={label}
              className={classes.sitemapLink}
            />
          )
          : (
            <EVChip
              label={label}
              className={classes.buildingChip}
            />
          )
      )}
    </>
  );
};

const getCostCellValue = ({
  statusID, subStatusID, orderHistoryDisplayCost, isBillingAffiliateUser, isEntitledForHidePricing,
}) => {
  if (isBillingAffiliateUser || isEntitledForHidePricing) {
    return '-';
  }

  const isCostVisible = Number(statusID) === STATUSES.Completed || Number(subStatusID) === SUB_STATUSES.ReadyToSend;
  return isCostVisible ? orderHistoryDisplayCost : '-';
};

const getStatusCell = ({
  statusID, subStatusID, primaryProductID, statusName, classes, t,
}) => {
  const pendingStatus = getPendingStatusName(Number(statusID), Number(subStatusID), Number(primaryProductID));
  const statusKey = pendingStatus ? `${statusID}_${pendingStatus}` : Number(statusID);
  return (
    <EVChip
      className={clsx(classes.statusChip, classes[STATUS_CLASS_NAMES[statusKey]])}
      label={pendingStatus ? t(pendingStatus) : statusName}
    />
  );
};

const getActionsCell = ({
  row, reportActions, isOrderingDisabled, isOrderHistoryEsxFilesUpgradeEnabled,
}) => {
  const {
    reportId, signedReportId, primaryProductID, statusID, subStatusID, address, city, state, zip,
  } = row;
  const showFilesEsxUpgrade = Boolean(
    isOrderHistoryEsxFilesUpgradeEnabled
    && reportActions?.canAddESX
    && !isOrderingDisabled,
  );
  return (
    <>
      {isCompletedOrReadyToCapturePayment(statusID, subStatusID) && (
      <DetailsButton
        reportId={reportId}
        primaryProductID={primaryProductID}
        showFilesEsxUpgrade={showFilesEsxUpgrade}
      />
      )}
      {
        (Number(statusID) === STATUSES.Pending && Number(subStatusID) === SUB_STATUSES.NeedToID)
         && <IdentifyButton reportId={reportId} signedReportId={signedReportId} />
      }
      {
        (Number(statusID) === STATUSES.Pending && Number(subStatusID) === SUB_STATUSES.SiteMap)
         && <SitemapApprovalButton reportId={reportId} />
      }
      {
        (primaryProductID === PRODUCT_IDS.BlueprintResidential)
        && (Number(subStatusID) === SUB_STATUSES.MeasurementData || Number(subStatusID) === SUB_STATUSES.CustomerResponse)
        && <UploadButton reportId={reportId} signedReportId={signedReportId} />
      }
      {
        reportActions?.canEdit
          && (primaryProductID !== PRODUCT_IDS.Roof2D || !reportActions.enableTrueDesignLink)
          && <EditPitchButton reportId={reportId} signedReportId={signedReportId} />
      }
      {isReportCancellable(statusID, subStatusID) && <CancelButton reportId={reportId} reportAddress={`${address}, ${city}, ${state} ${zip}`} />}
      {
       reportActions?.canUpgradeReport && !isOrderingDisabled && isCompletedOrReadyToCapturePayment(statusID, subStatusID)
        && <UpgradeButton reportId={reportId} />
      }
    </>
  );
};

const getProductCell = ({
  reportId, primaryProductID, reportActions, productDisplayLabel, showTrueDesignButton,
}) => (reportActions?.enableTrueDesignLink
  ? (
    <TrueDesignLink
      reportId={reportId}
      productName={productDisplayLabel}
      primaryProductID={primaryProductID}
      showTrueDesignButton={showTrueDesignButton}
    />
  ) : productDisplayLabel);

const getHasPropertyData = (propertyDataMap, reportId) => Object.keys(propertyDataMap?.[reportId] || {}).length > 0;

const getDisplayDelivery = ({
  primaryProductID, hasPropertyData, deliveryProductName,
}) => ((primaryProductID === PRODUCT_IDS.PropertyData || hasPropertyData) ? 'Instant' : deliveryProductName);

const getExpandedRow = ({
  row, claimInfo, searchText, hasPropertyData, completeAddress,
}) => {
  const {
    reportId,
    claimNumber,
    insuredName,
    catId,
    batchId,
    poNumber,
    dependentReportExternalReferenceId,
    policyNumber,
    dateOfLoss,
  } = row;

  return {
    reportId,
    ...(claimNumber && { claimNumber }),
    ...(claimInfo && { claimInfo: getHighlightedText(claimInfo, searchText) }),
    ...(insuredName && { insuredName }),
    ...(catId && { catId }),
    ...(batchId && { batchId }),
    ...(poNumber && { poNumber }),
    ...(dependentReportExternalReferenceId && { dependentReportExternalReferenceId }),
    ...(policyNumber && { policyNumber }),
    ...(dateOfLoss && { dateOfLoss: getHighlightedText(formatDate(dateOfLoss), formatDate(parseDateFromSearchText(searchText))) }),
    ...(completeAddress && { completeAddress }),
    ...(hasPropertyData && { hasPropertyData: true }),
  };
};

const getOrderHistoryCellsConfig = (args) => {
  const [
    reportDetailsList,
    t,
    classes,
    availableActions,
    isOrderingDisabled = false,
    isBillingAffiliateUser = false,
    isEntitledForHidePricing = false,
    searchText = '',
    showTrueDesignButton = false,
    propertyDataMap = {},
    isESXEnabled = false,
    isOrderHistoryEsxFilesUpgradeEnabled = false, // #genai
  ] = args;
  return {
    reportDetailsList,
    t,
    classes,
    availableActions,
    isOrderingDisabled,
    isBillingAffiliateUser,
    isEntitledForHidePricing,
    searchText,
    showTrueDesignButton,
    propertyDataMap,
    isESXEnabled,
    isOrderHistoryEsxFilesUpgradeEnabled,
  };
};

const getSubscriptionOrderHistoryCellsConfig = (args) => {
  const [
    reportDetailsList,
    t,
    classes,
    availableActions,
    isOrderingDisabled = false,
    subscriptionDetailsMap = {},
    isBillingAffiliateUser = false,
    isEntitledForHidePricing = false,
    searchText = '',
    showTrueDesignButton = false,
    propertyDataMap = {},
    isESXEnabled = false,
    isOrderHistoryEsxFilesUpgradeEnabled = false, // #genai
  ] = args;
  return {
    reportDetailsList,
    t,
    classes,
    availableActions,
    isOrderingDisabled,
    subscriptionDetailsMap,
    isBillingAffiliateUser,
    isEntitledForHidePricing,
    searchText,
    showTrueDesignButton,
    propertyDataMap,
    isESXEnabled,
    isOrderHistoryEsxFilesUpgradeEnabled,
  };
};

const buildOrderHistoryBaseCells = (row, config, { isSubscription = false } = {}) => {
  const {
    t,
    classes,
    availableActions,
    isOrderingDisabled,
    isBillingAffiliateUser,
    isEntitledForHidePricing,
    searchText,
    showTrueDesignButton,
    propertyDataMap,
    isOrderHistoryEsxFilesUpgradeEnabled,
  } = config;
  const {
    reportId,
    orderPlacedDate,
    claimNumber,
    claimInfo,
    deliveryProductName,
    orderHistoryDisplayCost,
    statusName,
    statusID,
    subStatusID,
    primaryProductID,
    address,
    city,
    state,
    zip,
  } = row;

  const productDisplayLabel = getOrderHistoryProductDisplayLabel(
    row.primaryProductName,
    row.orderHistoryReportType,
    row.addOnProductName,
    row.addOnProductID,
    primaryProductID,
  );
  const reportActions = availableActions[reportId];
  const addressCell = getAddressCell({
    row,
    t,
    classes,
    searchText,
    isSubscription,
  });
  const cost = getCostCellValue({
    statusID,
    subStatusID,
    orderHistoryDisplayCost,
    isBillingAffiliateUser,
    isEntitledForHidePricing,
  });
  const status = getStatusCell({
    statusID,
    subStatusID,
    primaryProductID,
    statusName,
    classes,
    t,
  });
  const actions = getActionsCell({
    row,
    reportActions,
    isOrderingDisabled,
    isOrderHistoryEsxFilesUpgradeEnabled,
  });
  const product = getProductCell({
    reportId,
    primaryProductID,
    reportActions,
    productDisplayLabel,
    showTrueDesignButton,
  });
  const completeAddress = address && city && state && zip ? `${address}, ${city}, ${state} ${zip}` : null;
  const hasPropertyData = getHasPropertyData(propertyDataMap, reportId);
  const delivery = getDisplayDelivery({
    primaryProductID,
    hasPropertyData,
    deliveryProductName,
  });
  const expandedRow = getExpandedRow({
    row,
    claimInfo,
    searchText,
    hasPropertyData,
    completeAddress,
  });

  return {
    rowKey: reportId,
    reportId,
    reportIdCell: getHighlightedText(String(reportId), searchText),
    product,
    address: addressCell,
    date: formatDate(orderPlacedDate),
    claimNumber: getHighlightedText(claimNumber, searchText),
    delivery,
    cost,
    status,
    actions,
    expandedRow,
  };
};

export const getInitialExpandedRowFlags = (dataList, searchText) => (searchText ? dataList?.reduce((acc, {
  reportId, claimNumber, claimInfo, insuredName,
  catId, batchId, poNumber, dependentReportExternalReferenceId, policyNumber, dateOfLoss,
}) => {
  const updatedDataList = [claimNumber, claimInfo, insuredName, catId, batchId, poNumber, dependentReportExternalReferenceId, policyNumber];
  return {
    ...acc,
    [reportId]: (updatedDataList.some((dataItem) => dataItem?.toLowerCase?.().includes(searchText.toLowerCase()))
    || (formatDate(dateOfLoss) === formatDate(parseDateFromSearchText(searchText)))),
  };
}, {}) : {});

/**
 * @param {Array} reportDetailsList - order history report details fetched from API
 * @param {*} t - translation function
 * @returns order history data to be rendered in table
 */
export const getOrderHistoryCells = (...args) => {
  const config = getOrderHistoryCellsConfig(args);
  return config.reportDetailsList.map((row) => buildOrderHistoryBaseCells(row, config));
};

/**
 * gets the page size and page number from url search params and returns after validation
 * @param {Array} reportIds List of report ids for order history page
 * @returns pageSize and pageNumber
 */
export const getPageOptionsFromURLParams = (reportIds) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlPageSize = Number(urlSearchParams.get('num_results'));

  // if pageSize from url is invalid, returns default value: 10
  const pageSize = PAGE_SIZE_OPTIONS.some((options) => options.id === urlPageSize) ? urlPageSize : 10;

  const pageCount = Math.ceil(reportIds.length / pageSize);

  // if pageNumber from url is invalid, returns default value: 1‚
  let pageNumber = 1;
  urlSearchParams.forEach((_, param) => {
    if (String(param).startsWith('page_')) {
      const urlPageNumber = Number(param.split('_')[1]);

      if (urlPageNumber <= pageCount && urlPageNumber > 0) { pageNumber = urlPageNumber; }
    }
  });

  return { pageSize, pageNumber };
};

/** Adds or updates the pageSize and pageNumber to the URL as search params */
export const setPageOptionsToURLParams = ({ pageSize, pageNumber }) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (pageNumber) {
    // remove previous page number from URL search params
    urlSearchParams.forEach((_, param) => {
      if (String(param).startsWith('page_')) urlSearchParams.delete(param);
    });

    urlSearchParams.set(`page_${pageNumber}`, 'null');
  }

  if (pageSize) urlSearchParams.set('num_results', pageSize);

  history.replace(`orders?${urlSearchParams.toString().replace('=null', '')}`);
};

export const getOrderHistoryLoadingCells = (reportDetailsList, isMobile, classes) => reportDetailsList.map((_, index) => ({
  expansionIcon: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  rowKey: index,
  reportId: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  product: <EVSkeleton
    className={classes.skeletonLoader}
    width={isMobile ? 250 : '14.69vw'}
    height={isMobile ? 18 : 27}
    animation="wave"
  />,
  address: <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 131 : '12.5vw'} height={isMobile ? 36 : 27} animation="wave" />,
  date: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  claimNumber: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  delivery: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  cost: <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 62 : 50} height={isMobile ? 24 : 27} animation="wave" />,
  status: <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 76 : '14.7vw'} height={isMobile ? 14 : 27} animation="wave" />,
}));

export const getFileTypesObjectFromList = (list) => list.reduce((obj, { reportID, reportFiles }) => ({ ...obj, [reportID]: reportFiles }), {});

export const getSearchQueryFromURLParams = () => new URLSearchParams(window.location.search).get('search');

export const setQueryValuesToURLParams = (key, value) => {
  const urlSearchParams = new URLSearchParams(window.location.search);

  if (value?.length) urlSearchParams.set(key, value);
  else urlSearchParams.delete(key);

  history.replace(`orders?${urlSearchParams.toString().replace('=null', '')}`);
};

/**
 * Filters the organization nodes based if the search text is present in the node name or any of its children.
 * @param {Object} rootNode - The organization node to filter.
 * @param {string} searchText - The search text to filter the nodes.
 * @returns {Array} - An array containing the filtered node and the expanded nodes.
 */
export const filterOrgNodes = (rootNode, searchText) => {
  // Return the root node without filtering if the search text is empty or less than 3 characters
  if (searchText.length < 3) return [rootNode, []];

  const { orgId, orgName, children } = rootNode;
  const expandedNodes = [];

  // Recursively filter the children nodes
  const filteredChildren = children.map((child) => {
    const [filteredChildChildren, expandedChildNodes] = filterOrgNodes(child, searchText);
    expandedNodes.push(...expandedChildNodes);
    return filteredChildChildren;
  }).filter(Boolean);

  if (filteredChildren.length > 0) expandedNodes.push(String(orgId));

  if (
    orgName.toLowerCase().includes(searchText.toLowerCase()) || filteredChildren.length > 0
  ) return [{ ...rootNode, children: filteredChildren }, expandedNodes];

  return [null, []];
};

/**
 * Returns the parent nodes of the selected node IDs.
 * @param {Object} rootNode - The organization node to traverse.
 * @param {Array} selectedNodeIds - An array of selected node IDs.
 * @returns {Array} - An array of parent node IDs.
 */
export const getSelectedParentNodeIds = (rootNode, selectedNodeIds) => {
  const parentNodes = new Set();

  const findParentNodes = (node, parentId = null) => {
    const { orgId, children } = node;
    if (selectedNodeIds.includes(orgId) && parentId !== null) {
      parentNodes.add(String(parentId));
    }
    children?.forEach((child) => {
      findParentNodes(child, orgId);
    });

    if (parentNodes.has(String(orgId)) && parentId !== null) parentNodes.add(String(parentId));
  };

  findParentNodes(rootNode);
  return Array.from(parentNodes);
};

export const getStatusValues = (t) => ({
  Created: {
    name: t('common__created'),
    color: '#B56114',
    backgroundColor: '#FFECB7',
  },
  InProcess: {
    name: t('common__in_process'),
    color: '#B56114',
    backgroundColor: '#FFECB7',
  },
  ActionNeeded: {
    name: t('common__action_needed'),
    color: '#A60064',
    backgroundColor: '#FFBCD0',
  },
  CheckEmail: {
    name: t('common__check_email'),
    color: '#FFBCD0',
    backgroundColor: '#A60064',
  },
  Closed: {
    name: t('common__closed'),
    color: '#4D5365',
    backgroundColor: '#C8CDD7',
  },
  Completed: {
    name: t('common__completed'),
    color: '#1F6800',
    backgroundColor: '#E2F7DA',
  },
});

/**
 * Updates the selected values array with the latest value.
 * @param {Array} selectedValues - The array of selected values.
 * @param {*} latestValue - The latest value to be added or removed from the selected values array.
 * @returns {Array} - The updated array of selected values.
 */
export const updateSelectedValues = (selectedValues, latestValue) => {
  const currentSelectedValues = [...selectedValues];

  if (currentSelectedValues.length === 0) return [latestValue];

  const duplicateSelectedValueIndex = currentSelectedValues.findIndex((item) => isEqual(item, latestValue));

  if (duplicateSelectedValueIndex !== -1) currentSelectedValues.splice(duplicateSelectedValueIndex, 1);
  else currentSelectedValues.push(latestValue);

  return currentSelectedValues;
};

// #genai Flatten PrimaryProducts for OH/Analytics filters; drop delivery, integration fees, ESX
export const getProductCatalogFilterOptions = (products, { includeCategoryMeta = false } = {}) => {
  const options = Object.keys(products || {}).flatMap((category) => (
    products[category]?.PrimaryProducts?.map((product) => ({
      id: product?.ProductID,
      // #genai Roof 106/109 get distinct labels even when API Name is "Roof"
      name: getOrderHistoryProductFilterDisplayName(product?.ProductID, product?.Name),
      ...(includeCategoryMeta ? {
        reportType: product?.Description,
        // #genai Positive condition preserves the same category-to-null mapping.
        structureType: category === 'NotSet' ? null : category,
      } : {}),
    })) || []
  ));
  return options
    .filter((product) => product?.id != null && !PRODUCT_CATALOG_FILTER_EXCLUDED_IDS.has(product.id))
    .sort((product1, product2) => product1?.name?.localeCompare(product2?.name));
};

export const getProductsList = (products) => getProductCatalogFilterOptions(products).map((product) => product.id);

// #genai Expand OH filter productIds with Walls add-on PIDs for report-ids only (not Redux/URL)
export const expandProductIdsWithWallsAddOns = (productIds = []) => {
  const expanded = new Set(productIds);
  productIds.forEach((id) => {
    const addOnId = getWallsAddOnIdByProductId(id);
    if (addOnId) expanded.add(addOnId);
  });
  return [...expanded];
};

export const getAllOrgIds = (orgNodes) => (orgNodes?.orgId
  ? [orgNodes.orgId, ...(orgNodes.children || []).flatMap(getAllOrgIds)] // Recursively get all ids
  : []); // If no id, return empty array

/**
 * @param {Array} reportDetailsList - order history report details fetched from API
 * @param {*} t - translation function
 * @returns order history data to be rendered in table (Subscription variant with combined cells)
 */
export const getSubscriptionOrderHistoryCells = (...args) => {
  const config = getSubscriptionOrderHistoryCellsConfig(args);
  return config.reportDetailsList.map((row) => {
    const baseCells = buildOrderHistoryBaseCells(row, config, { isSubscription: true });
    const subscriptionDetailsData = config.subscriptionDetailsMap?.[row.reportId];
    const subscriptionDetails = subscriptionDetailsData ? (
      <SubscriptionCell subscriptionDetailsData={subscriptionDetailsData} currencyTypeId={row.currencyTypeId} />
    ) : null;
    const productAndAddress = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="body-600" fontColor="ocean-600">
          {baseCells.product}
        </Typography>
        <Typography variant="subtitle-400" fontColor="charcoal-700">
          {baseCells.address}
        </Typography>
      </div>
    );
    const statusAndActions = (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: 'max-content',
        alignItems: 'center',
      }}
      >
        {baseCells.status}
        {baseCells.actions}
      </div>
    );

    return {
      ...baseCells,
      productAndAddress,
      subscriptionDetails,
      statusAndActions,
    };
  });
};

export const getSubscriptionOrderHistoryLoadingCells = (reportDetailsList, isMobile, classes) => reportDetailsList.map((_, index) => ({
  rowKey: index,
  isLoading: true,
  productAndAddress: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 250 : 200} height={isMobile ? 18 : 22} animation="wave" />
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 131 : 180} height={isMobile ? 36 : 18} animation="wave" />
    </div>
  ),
  reportIdCell: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  claimNumber: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  date: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  delivery: <EVSkeleton className={clsx(classes.skeletonLoader, classes.loaderSizeMedium)} animation="wave" />,
  subscriptionDetails: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 120 : 150} height={isMobile ? 14 : 20} animation="wave" />
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 80 : 100} height={isMobile ? 14 : 20} animation="wave" />
    </div>
  ),
  statusAndActions: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 76 : 100} height={isMobile ? 14 : 27} animation="wave" />
      <EVSkeleton className={classes.skeletonLoader} width={isMobile ? 76 : 80} height={isMobile ? 14 : 27} animation="wave" />
    </div>
  ),
  expandedRow: null,
}));
